import React, { useState } from 'react';
import { t } from '@lingui/macro';
import { useCurrentAgreement, useCurrentLocale, useLocalStorage } from '@economic/react-app';
import { Button, Table3, IconButton } from '@economic/taco';
import EmptyStateInitializer from '~/app/shared/table3tools/components/EmptyStateInitializer';
import { ProductListView } from '../../../types/internal-api';
import { ViewProps } from '../types';
import { CreateProductDialog } from '../../forms/Inventory/CreateOrEditProduct';
import useProductsViewTable3DataContextLoader from './hooks/useProductsViewTable3DataContextLoader';

interface Props extends ViewProps<ProductListView> {
    showPrice?: boolean;
    showCreateOnTheFly?: boolean;
    showInventoryModule?: boolean;
}

export default function ProductsView({
    onCreated: handleCreated,
    onSelected: handleSelected,
    showPrice,
    showCreateOnTheFly: showCreateOnTHeFly,
    showInventoryModule,
}: Props) {
    const agreement = useCurrentAgreement();
    const locale = useCurrentLocale();
    const context = useProductsViewTable3DataContextLoader();

    const hasInventoryModule = showInventoryModule ?? !!agreement.modules.inventory;

    const currencyKey = `${agreement.number}/sales.product.currency`;
    const localeCurrency = locale.currencyCode;

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [currency] = useLocalStorage<string>(currencyKey, localeCurrency);

    const showSecondCurrency = !!localeCurrency && !!currency && localeCurrency !== currency;

    const rowActions = [
        row => (
            <IconButton
                aria-label={t`Inventory matrix`}
                icon="inventory-matrix"
                onClick={event => {
                    event.stopPropagation();
                    window.top?.PopUp2(
                        `../lager/varianter_tast.asp?showzero=yes&VareNr=${row.productNumber.replace(/\+/g, '%2B')}`,
                        750,
                        500
                    );
                }}
                tooltip={t`Inventory matrix`}
            />
        ),
    ];

    const createButton = (
        <Button appearance="primary" onClick={() => setShowCreateModal(true)}>
            {t`Create product`}
        </Button>
    );

    const handleRowClick = (product: ProductListView) => {
        handleSelected(product);
    };

    return (
        <>
            <h1>{t`Products`}</h1>
            <Table3<ProductListView>
                id={`products-view-table${showInventoryModule ? '-with-inventory' : ''}`}
                data={context.data}
                ref={context.tableRef}
                length={context.length}
                loadAll={context.loadAll}
                loadPage={context.loadPage}
                onChangeFilter={context.onChangeFilter}
                onChangeSort={context.onChangeSort}
                pageSize={context.pageSize}
                preset="complex"
                onRowClick={handleRowClick}
                enableFiltering={false}
                emptyState={() => <EmptyStateInitializer hasInitialized={context.hasInitialized} />}
                rowActions={hasInventoryModule ? rowActions : undefined}
                toolbarLeft={showCreateOnTHeFly ? createButton : undefined}
                enableSorting>
                <Table3.Column id="productNumber" accessor="productNumber" header={t`No.`} sort="asc" />
                <Table3.Column id="name" accessor="name" align="left" header={t`Name`} />
                {showPrice ? <Table3.Column id="salesPrice" accessor="salesPrice" align="right" header={t`Sales Price`} /> : null}
            </Table3>
            {showCreateModal && (
                <CreateProductDialog
                    onClose={() => setShowCreateModal(false)}
                    showSaveAndNew={false}
                    onSubmitted={values => handleCreated && handleCreated(values)}
                    showSecondSalesPrice={showSecondCurrency}
                    baseCurrency={currency}
                    defaultOpen
                />
            )}
        </>
    );
}
